/**
 * Created by WorkAxis www.workaxis.in
 * Developer: Padma Dev.E
 * Developer Contact: padmadev.e@workaxis.in
 * Admin: shyam@workaxis.in
 * Date: 3/14/2022
 * Time: 2:03 PM
 * Project: w_crm
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { authState, checkAuth, refreshTokenApi, refreshAllAccounts } from "../redux/auth/authReducer";
import { ChangeClientLayoutState, getProfileApi, profileState, updateToken, } from "../redux/profile/profileReducer";
import { eraseCookie, getCookie, setCookie } from "../utils/cookies";
import { isJwtValid } from "../utils/common";
import { ACCOUNTS_COOKIE_KEY, ACTIVE_ACCOUNT_COOKIE_KEY } from "../utils/constants";
import { SyncState } from "../redux/sync/syncReducer";
import PrivateMainContainer from "../layouts/PrivateMainContainer/PrivateMainContainer";
import Spinner from "../component/Spinner/Spinner";
import { getFirebaseToken, getMessageInstance } from "../firebase/firebase";
import { onMessage } from "firebase/messaging";
import {
    connectRealtimeSocket,
    disconnectRealtimeSocket,
    handleCampaignJobRealtime,
    setRealtimeMessageHandler,
} from "../api/realtimeSocket";
import {
    WhatsappChatListUpdateItem,
    WhatsappMessageIncoming,
    WhatsappState,
} from "../redux/Integration/whatsappReducer";
import { Toast } from "../component/Toast/Toast";

export default function PrivateLayout({
    component: Component,
    configuration,
    ...rest
}) {
    let navigate = useNavigate();
    let dispatch = useDispatch();
    useEffect(() => {
        dispatch(checkAuth());
    }, [dispatch]);
    const { profile } = useSelector(profileState);
    const { current: sync } = useSelector(SyncState);
    const { activeChat } = useSelector(WhatsappState);
    // Ref so the global socket handler sees the latest open chat without reconnecting
    const activeChatRef = useRef(activeChat);
    const [lastSync, setLastSync] = useState(0);

    useEffect(() => {
        activeChatRef.current = activeChat;
    }, [activeChat]);

    useEffect(() => {
        if (lastSync !== sync.time) {
            setLastSync(sync.time);
            // handleGetFirebaseToken()
        }
    }, [sync.time, lastSync]);

    const { isAuthenticated, isLoading } = useSelector(authState);

    const [refreshRetry, setRefreshRetry] = useState(0);

    useEffect(() => {
        dispatch(ChangeClientLayoutState(false))
    }, []);

    useEffect(() => {
        const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
        let accessToken = null;
        let refreshToken = null;
        if (activeId) {
            try {
                const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                const acc = accounts.find((acc) => acc.id.toString() === activeId.toString());
                if (acc) {
                    accessToken = acc.access;
                    refreshToken = acc.refresh;
                }
            } catch (e) { }
        }
        if (!accessToken || !isJwtValid(accessToken)) {
            if (!refreshToken || !isJwtValid(refreshToken)) {
                try {
                    const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
                    const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                    const remaining = activeId ? accounts.filter((acc) => acc.id.toString() !== activeId.toString()) : accounts;
                    if (remaining.length > 0) {
                        const nextAccount = remaining[remaining.length - 1];
                        setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(remaining), 365);
                        setCookie(ACTIVE_ACCOUNT_COOKIE_KEY, nextAccount.id, 365);
                        window.location.reload();
                    } else {
                        navigate('/login', { replace: true });
                    }
                } catch (e) {
                    navigate('/login', { replace: true });
                }
            } else {
                try {
                    if (refreshRetry < 3) {
                        setRefreshRetry(old => old + 1)
                        dispatch(refreshTokenApi({ refresh: refreshToken }))
                    } else {
                        // Clear active account if refresh failed too many times
                        if (activeId) {
                            try {
                                const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
                                const newAccounts = accounts.filter((acc) => acc.id !== activeId);
                                if (newAccounts.length > 0) setCookie(ACCOUNTS_COOKIE_KEY, JSON.stringify(newAccounts), 365);
                                else eraseCookie(ACCOUNTS_COOKIE_KEY);
                                eraseCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
                            } catch (e) { }
                        }
                    }
                } catch (error) {
                    // dispatch(masterLogout());
                    navigate('/login', { replace: true });
                }
            }
        } else if (!profile.data && !profile.isLoading && !isAuthenticated) {
            dispatch(getProfileApi());
        }
    }, [isAuthenticated, dispatch, navigate, isLoading, refreshRetry, profile.data]);


    // Global user-scoped WebSocket (chat + reminders) -------------------
    useEffect(() => {
        if (!isAuthenticated || !profile.data) {
            return undefined;
        }

        setRealtimeMessageHandler((event) => {
            if (!event || typeof event !== "object") return;
            const eventType = event.type;
            const data = event.data;

            // Legacy shape: { data: message } without type
            if (!eventType && data) {
                dispatch(WhatsappMessageIncoming(event));
                return;
            }

            if (eventType === "whatsapp.message" || eventType === "whatsapp.status") {
                const activeId = activeChatRef.current?.clientId;
                const msgClientId = data?.crm_client_id;
                // Update open thread only when message is for the focused client
                if (
                    activeId &&
                    (!msgClientId || String(msgClientId) === String(activeId))
                ) {
                    dispatch(WhatsappMessageIncoming({data}));
                } else if (eventType === "whatsapp.status" && activeId) {
                    dispatch(WhatsappMessageIncoming({data}));
                }
                return;
            }

            if (eventType === "chat.upsert" && data) {
                const typeId = data.type_id || data.id;
                if (!typeId) return;
                const activeId = activeChatRef.current?.clientId;
                // Active thread: clear unread. Inactive: ensure badge bumps even if BE omitted unread.
                const isActiveChat =
                    activeId && String(activeId) === String(typeId);
                const payload = {
                    ...data,
                    name:
                        data.name ||
                        [data.first_name, data.last_name]
                            .filter(Boolean)
                            .join(" ")
                            .trim() ||
                        undefined,
                };
                if (isActiveChat) {
                    payload.unread = 0;
                } else if (payload.unread == null) {
                    payload._bumpUnread = true;
                }
                dispatch(
                    WhatsappChatListUpdateItem({
                        id: typeId,
                        payload,
                    })
                );
                return;
            }

            if (eventType === "notification.reminder" && data) {
                Toast(data.title || "Reminder", "info");
                return;
            }

            // AI campaign async generate / edit / regenerate
            if (eventType === "campaign.job" && data) {
                handleCampaignJobRealtime(data);
            }
        });

        connectRealtimeSocket();

        return () => {
            setRealtimeMessageHandler(null);
            disconnectRealtimeSocket();
        };
    }, [isAuthenticated, profile.data, dispatch]);

    // FCM starts -------------------
    useEffect(() => {
        onMessage(getMessageInstance(), {
            next: (payload) => {
                const { data: { destination, title } } = payload;
                const notification = new Notification(title);
                if (destination) {
                    notification.onclick = function () {
                        navigate(`/contact/${destination}#whatsapp`)
                        notification.close()
                    };
                }
            },
            error: (error) => {
            },
            complete: () => {
            }
        });
    }, []);

    // Refresh all accounts in background
    useEffect(() => {
        dispatch(refreshAllAccounts());
        const interval = setInterval(() => {
            dispatch(refreshAllAccounts());
        }, 5 * 60 * 1000); // 5 minutes
        return () => clearInterval(interval);
    }, [dispatch]);


    const handleGetFirebaseToken = () => {
        // registerServiceWorker()
        getFirebaseToken()
            .then((firebaseToken) => {
                dispatch(updateToken({ token: firebaseToken }))
            })
            .catch((err) => console.error('An error occured while retrieving firebase token. ', err))
    }


    const initFB = () => {
        // console.log("init")
        window.fbAsyncInit = function () {
            window.FB.init({
                appId: '348366554900749',
                autoLogAppEvents: true,
                xfbml: true,
                version: process.env.REACT_APP_META_API_VERSION || 'v20.0'
            });
        };
    }

    // FCM end -------------------

    return (
        <>
            <Spinner isSpinning={profile.isLoading || isLoading}>
                {profile.data && (
                    <PrivateMainContainer
                        lastSync={lastSync}
                        component={Component}
                        configuration={configuration}
                    />
                )}
            </Spinner>
        </>
    );
}
